import { FC, ReactNode } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { statusTones } from '../../themes/brandTokens';

interface LabelProps {
  className?: string;
  color?:
    | 'primary'
    | 'black'
    | 'secondary'
    | 'error'
    | 'warning'
    | 'success'
    | 'info';
  children?: ReactNode;
}

const tone = (name: keyof typeof statusTones) => `
          background-color: ${statusTones[name].bg};
          color: ${statusTones[name].text};

          &::before {
            background-color: ${statusTones[name].dot};
          }
`;

// A status pill: tinted background, a coloured dot, the label in regular weight. Icons that
// callers pass are hidden so every badge in the admin reads the same way.
const LabelWrapper = styled('span')(
  ({ theme }) => `
      display: inline-flex;
      align-items: center;
      gap: 6px;
      height: 24px;
      padding: 0 10px 0 8px;
      border-radius: 9999px;
      font-size: ${theme.typography.pxToRem(12.5)};
      font-weight: 500;
      line-height: 1;
      letter-spacing: 0;
      white-space: nowrap;
      vertical-align: middle;

      &::before {
        content: '';
        flex-shrink: 0;
        width: 6px;
        height: 6px;
        border-radius: 50%;
      }

      .MuiSvgIcon-root {
        display: none;
      }

      b, strong {
        font-weight: inherit;
        font-size: inherit !important;
      }

      &.MuiLabel {
        &-primary { ${tone('primary')} }
        &-black { ${tone('black')} }
        &-secondary { ${tone('secondary')} }
        &-success { ${tone('success')} }
        &-warning { ${tone('warning')} }
        &-error { ${tone('error')} }
        &-info { ${tone('info')} }
      }
`
);

// Mapped statuses are stored in capitals: shown in sentence case, acronyms kept.
const ACRONYMS = new Set(['KYT', 'KYC', 'API', 'UUID', 'IBAN']);
export const statusText = (text: string) =>
  String(text || '')
    .split(/\s+/)
    .map((word, index) => {
      const upper = word.toUpperCase();
      if (ACRONYMS.has(upper)) return upper;
      const lower = word.toLowerCase();
      return index === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(' ');

const Label: FC<LabelProps> = ({
  className,
  color = 'secondary',
  children,
  ...rest
}) => {
  return (
    <LabelWrapper className={'MuiLabel-' + color} {...rest}>
      {children}
    </LabelWrapper>
  );
};

Label.propTypes = {
  // @ts-ignore
  children: PropTypes.node,
  className: PropTypes.string,
  color: PropTypes.oneOf([
    'primary',
    'black',
    'secondary',
    'error',
    'warning',
    'success',
    'info'
  ])
};

export default Label;
